import React from 'react';
import {
  ButtonWithAction,
  ButtonWithLink,
  SidebarSingleContent,
  Box,
  Cluster,
  constants,
  Center,
  DisplayBox,
  Stack,
  Paragraph,
  SolidDivider,
  Title,
  // Spinner,
} from '@thecb/components';
import AlertBar from '../../components/alert-bar';
import CartSidebar from '../../components/cart-sidebar';
import { SearchResultsTable } from './SearchResultsTable';
import { formatPrice } from '../../utils/helpers';
import { addToCartRequest } from '../lookup/Lookup.state';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

const Header = ({ alertBar, alertBarActions }) => (
  <Box width="100%" padding="16px 0">
    <Center maxWidth="85.714rem">
      {alertBar.alerts.length > 0 && (
        <Box padding="16px 0">
          <AlertBar {...alertBar} {...alertBarActions} />
        </Box>
      )}
      <Title
        color={constants.colors.CHARADE_GREY}
        weight={constants.fontWeights.FONT_WEIGHT_BOLD}
      >
        Add Items
      </Title>
    </Center>
  </Box>
);

const SearchResults = ({
  alertBars,
  actions,
  searchResults,
  selectedResults,
  selectedResultsTotal,
  accountData,
  updateResultAmount,
  updateResultAnnotation,
  toggleResultSelect,
  toggleSelectAll,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentProduct = useSelector((state) => state.products.currentProduct);
  const { annotation_allowed } = currentProduct.product_data;
  const annotationFields = annotation_allowed
    ? currentProduct.annotation_fields || []
    : [];
  const requiredAnnotationFields = annotationFields.filter((f) => f.required);

  const hasIncompleteAnnotations =
    requiredAnnotationFields.length > 0 &&
    selectedResults.some((result) =>
      requiredAnnotationFields.some(
        (field) => !result.annotations?.[field.name]?.trim(),
      ),
    );

  if (searchResults.length < 1) return null;
  const handleAddToCartClick = () => {
    const receivables = selectedResults.map((result) => ({
      description: result.description,
      recorded_subtotal: result.amount,
      recorded_price: result.recorded_price || result.amount,
      item_id: result.item_id,
      artifact_items: {},
      external_attributes: {
        ...result.annotations,
        ...result.external_attributes,
      },
      external_uid: result.external_uid,
      validated: result.validated,
      accepts_underpayment: result.accepts_underpayment,
      accepts_overpayment: result.accepts_overpayment,
      receivable_category: result.receivable_category,
    }));
    dispatch(addToCartRequest({ receivables, navigate, location }));
  };
  const accountInfo = (
    <>
      {accountData && (
        <Box
          padding="0.5rem"
          background="#eee"
          data-testid="search-results-account-info"
        >
          <Title
            as="h3"
            variant="small"
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
          >
            Personal Information
          </Title>
          <Box padding="0px 0px 0px 4px">
            <Stack direction="column" childGap="0" width="50%">
              {Object.entries(accountData).map(([key, value]) => (
                <Stack direction="row" key={key}>
                  <Box width="8rem" padding="0">
                    <Paragraph>{key}:</Paragraph>
                  </Box>
                  <Box padding="0">
                    <Paragraph>{value}</Paragraph>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      )}
    </>
  );

  const content = (
    <DisplayBox padding="16px 0">
      {accountInfo}
      <Box padding="1rem 0" id="search-results-body">
        <SolidDivider />
        <SearchResultsTable
          actions={{
            toggleSelectAll,
            toggleResultSelect,
            updateResultAmount,
            updateResultAnnotation,
          }}
          selectedTotal={selectedResultsTotal}
          data={searchResults}
          selected={selectedResults}
        />
      </Box>
      <SolidDivider />
      <Box padding="1rem 0 0 0">
        <Cluster justify="space-between" align="center">
          <Paragraph>
            Selected Total: {formatPrice(selectedResultsTotal)}
          </Paragraph>
          <Cluster justify="flex-end" align="center">
            <ButtonWithLink
              extraStyles={'border-radius: 4px;'}
              url="/pos"
              text="Back"
              variant="secondary"
              textExtraStyles="font-size: 1rem;"
            />
            <ButtonWithAction
              extraStyles={`
                border-radius: 4px;
                display: border-box;
              `}
              text="Add To Cart"
              variant="primary"
              disabled={
                selectedResults.length === 0 || hasIncompleteAnnotations
              }
              action={handleAddToCartClick}
              dataQa="add-to-cart"
              textExtraStyles="font-size: 1rem;"
            />
          </Cluster>
        </Cluster>
      </Box>
    </DisplayBox>
  );

  return (
    <SidebarSingleContent
      header={
        <Header
          key="SearchResultsHeader"
          alertBar={alertBars.searchResultsAlertBars}
          alertBarActions={actions.alertBars.searchResultsAlertBars}
        />
      }
      mainContent={content}
      sidebarContent={<CartSidebar />}
      sidebarTargetWidth="27.429rem"
      sidebarWrapperMaxWidth="85.714rem"
      sidebarOnRight={true}
      childGap="0.5rem"
    />
  );
};

export default SearchResults;
